import { IWebcamItem } from 'api/region/types';
import classNames from 'classnames';
import { WebcamInfo } from 'components/common/WebcamItem';
import React, { useCallback, useMemo } from 'react';
import { SwiperComponent, SwiperSlideItem } from 'components/blocks/SwiperComponent';
import { formatAgoIfIn24TimestampOrString } from 'util/formatAgo';
import { snowDaysAgo } from 'util/snowDaysAgo';
import { IResortPathInfo } from 'api/resort/types';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
import useWindowDimensions from 'util/useWindowDimensions';
import styles from './styles.module.scss';

const MD_BREAKPOINT = 768;

const cameraSliderBreakpoints = {
  375: { slidesPerView: 1.2 },
  640: { slidesPerView: 1.7 },
};

export interface IProps {
  isFresh: boolean;
  cameras: Array<IWebcamItem & { resort?: IResortPathInfo, regionName?: string }>;
  onCamSelect?: (cam: IWebcamItem) => void;
  currentId?: string;
  showSummitDepth?: boolean;
  showSnowReport?: boolean;
  imageLink?: (r: IResortPathInfo) => string,
  titleLink?: (r: IResortPathInfo) => string,
  skipSingleCamera: boolean,
  startAllLiveOnMobile?:boolean,
  className?: string,
  showAsSlider?: boolean,
  isResortCams?: boolean,
}

export const CameraList = ({
  cameras,
  isFresh,
  onCamSelect,
  currentId,
  showSnowReport,
  showSummitDepth,
  imageLink,
  titleLink,
  skipSingleCamera,
  startAllLiveOnMobile,
  className,
  showAsSlider = false,
  isResortCams = false,
}: IProps) => {
  const onCamSelectCb = useCallback((cam?: IWebcamItem) => (cam ? onCamSelect?.(cam) : undefined), [onCamSelect]);

  const cameraIdsWithSnowInLast7Days = new Set(cameras.filter((camera) => {
    const snowDate = camera.snow?.date;

    if (!snowDate) {
      return false;
    }

    const daysAgo = snowDaysAgo(snowDate);

    return Number.isFinite(daysAgo) && daysAgo <= 7;
  }).map((c) => c.uuid));

  const { width } = useWindowDimensions();
  const shouldUseSlider = showAsSlider && width > 0 && width < MD_BREAKPOINT;

  const shouldSkipRender = skipSingleCamera ? (cameras.length <= 1) : (cameras.length < 1);

  const slides: SwiperSlideItem[] = useMemo(() => cameras.map((cam) => ({
    key: cam.uuid,
    content: (
      <WebcamInfo
        blur={!isFresh}
        imageLink={imageLink}
        titleLink={titleLink}
        showSnowReport={showSnowReport && cameraIdsWithSnowInLast7Days.has(cam.uuid)}
        showSummitDepth={showSummitDepth}
        onClick={onCamSelectCb}
        cam={cam}
        regionName={cam.regionName}
        subtitle={formatAgoIfIn24TimestampOrString(cam.date)}
        title={cam.title}
        isCurrent={cam.uuid === currentId}
        startAllLiveOnMobile={startAllLiveOnMobile}
        isResortCams={isResortCams}
      />
    ),
  })), [
    cameras,
    isFresh,
    imageLink,
    titleLink,
    showSnowReport,
    cameraIdsWithSnowInLast7Days,
    showSummitDepth,
    onCamSelectCb,
    currentId,
    startAllLiveOnMobile,
    isResortCams,
  ]);

  if (shouldSkipRender) {
    return null;
  }

  if (shouldUseSlider) {
    return (
      <SwiperComponent
        slides={slides}
        spaceBetween={16}
        breakpoints={cameraSliderBreakpoints}
        navigationThreshold={1}
        wrapperClassName={classNames(styles.sliderContainer, isFresh ? undefined : BLUR_CONTENT_STYLE_NAME)}
        sliderWrapperClassName={styles.sliderWrapper}
        swiperClassName={styles.mobileSwiper}
        slideClassName={styles.mobileSlide}
        navButtonClassName={styles.swiperNavButton}
        prevButtonClassName={styles.swiperButtonPrev}
        nextButtonClassName={styles.swiperButtonNext}
      />
    );
  }

  return (
    <div className={classNames(styles.box, className, isFresh ? '' : BLUR_CONTENT_STYLE_NAME)}>
      {cameras.map((cam) => (
        <WebcamInfo
          blur={!isFresh}
          imageLink={imageLink}
          titleLink={titleLink}
          showSnowReport={showSnowReport && cameraIdsWithSnowInLast7Days.has(cam.uuid)}
          showSummitDepth={showSummitDepth}
          onClick={onCamSelectCb}
          cam={cam}
          regionName={cam.regionName}
          subtitle={formatAgoIfIn24TimestampOrString(cam.date)}
          title={cam.title}
          key={cam.uuid}
          isCurrent={cam.uuid === currentId}
          startAllLiveOnMobile={startAllLiveOnMobile}
          isResortCams={isResortCams}
        />
      ))}
    </div>
  );
};
